import isPropValid from '@emotion/is-prop-valid';
import { cn } from '@skupreme/shared/ui';
import {
  forwardRef,
  type CSSProperties,
  type HTMLAttributes,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import './AppTable.css';

/*
 * Native table parts in the kit's table look, for markup the kit's React Aria
 * Table cannot host: rows the screens render themselves and hand to AppTable.
 * They take the Semantic UI table props the screens were written against, so
 * a Semantic `Table` cell `<… textAlign="center" width={3}>` becomes an
 * `<AppTableCell textAlign="center" width={3}>` and nothing else changes.
 *
 * The base look (padding, rules, header ink, sticky header, hover) lives in
 * AppTable.css under `.sk-app-table`, because it depends on where a part sits
 * (a header cell in a footer, the first body row); the props map to `sk:`
 * utilities on the element itself.
 */

const WIDTH_WORDS = [
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
] as const;

type WidthNumber =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16;

/** Semantic UI's 16-unit column grid: `3`, `'3'` or `'three'`. */
export type AppTableWidth =
  | WidthNumber
  | `${WidthNumber}`
  | (typeof WIDTH_WORDS)[number];

export type AppTableTextAlign = 'left' | 'center' | 'right' | 'justified';
export type AppTableVerticalAlign = 'top' | 'middle' | 'bottom';

/** A column's share of the table, from Semantic's 16-unit grid. */
export function appTableWidthPercent(
  width: AppTableWidth | number | string | undefined,
): string | undefined {
  if (width === undefined || width === null || width === '') {
    return undefined;
  }

  const units =
    typeof width === 'number'
      ? width
      : /^\d+$/.test(width)
        ? Number(width)
        : WIDTH_WORDS.indexOf(width as (typeof WIDTH_WORDS)[number]) + 1;

  if (!units || units < 1 || units > 16) {
    return undefined;
  }

  return `${((units / 16) * 100).toFixed(4)}%`;
}

const TEXT_ALIGN: Record<AppTableTextAlign, string> = {
  left: 'sk:text-left',
  center: 'sk:text-center',
  right: 'sk:text-right',
  justified: 'sk:text-justify',
};

const VERTICAL_ALIGN: Record<AppTableVerticalAlign, string> = {
  top: 'sk:align-top',
  middle: 'sk:align-middle',
  bottom: 'sk:align-bottom',
};

type ToneProps = {
  /** Success tint. */
  positive?: boolean;
  /** Danger tint; `error` is Semantic's synonym. */
  negative?: boolean;
  error?: boolean;
  warning?: boolean;
  /** The chosen row or cell. */
  active?: boolean;
  disabled?: boolean;
};

type AlignProps = {
  textAlign?: AppTableTextAlign;
  verticalAlign?: AppTableVerticalAlign;
  /** Keeps the content on one line. */
  singleLine?: boolean;
};

type CellLayoutProps = AlignProps & {
  /** Semantic took strings here too (`colSpan="2"`). */
  colSpan?: number | string;
  rowSpan?: number | string;
  /** Semantic's 16-unit grid, as a percentage of the table. */
  width?: AppTableWidth;
  /** As narrow as the content allows. */
  collapsing?: boolean;
  /** A pointer: the cell is a click target. */
  selectable?: boolean;
};

function toneClasses({
  positive,
  negative,
  error,
  warning,
  active,
  disabled,
}: ToneProps) {
  return cn(
    positive && 'sk:bg-success-bg sk:text-success',
    (negative || error) && 'sk:bg-danger-bg sk:text-danger',
    warning && 'sk:bg-warning-bg sk:text-warning',
    active && 'sk:bg-fill-tertiary',
    disabled && 'sk:pointer-events-none sk:opacity-50',
  );
}

function alignClasses({ textAlign, verticalAlign, singleLine }: AlignProps) {
  return cn(
    textAlign && TEXT_ALIGN[textAlign],
    verticalAlign && VERTICAL_ALIGN[verticalAlign],
    singleLine && 'sk:whitespace-nowrap',
  );
}

/*
 * Screens wrap these in styled-components with styling props of their own
 * (`expand`, `up`, `isActive`); those stop here instead of reaching the DOM.
 */
function domProps<T extends object>(props: T): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (isPropValid(key)) {
      out[key] = value;
    }
  }
  return out as T;
}

function widthStyle(
  width: AppTableWidth | undefined,
  style: CSSProperties | undefined,
): CSSProperties | undefined {
  const percent = appTableWidthPercent(width);
  if (!percent) {
    return style;
  }
  return { width: percent, ...style };
}

/* ---------------------------------------------------------------- root -- */

export type AppTableRootProps = Omit<
  TableHTMLAttributes<HTMLTableElement>,
  'align' | 'border' | 'width'
> &
  AlignProps & {
    /** No card frame; `'very'` also drops the side padding. */
    basic?: boolean | 'very';
    /** Rules between columns too. */
    celled?: boolean;
    /** Semantic's rowspan layout; drawn celled. */
    structured?: boolean;
    compact?: boolean | 'very';
    /** Alternate rows tinted. */
    striped?: boolean;
    /** Rows tint on hover. */
    selectable?: boolean;
    /** Columns keep their widths whatever the content. */
    fixed?: boolean;
    /** As narrow as the content allows. */
    collapsing?: boolean;
    /** Kept for call sites written against Semantic; they draw nothing. */
    padded?: boolean | 'very';
    unstackable?: boolean;
    stackable?: boolean;
    sortable?: boolean;
    definition?: boolean;
    inverted?: boolean;
    attached?: boolean | 'top' | 'bottom';
    size?: string;
    color?: string;
    columns?: number | string;
  };

export const AppTableRoot = forwardRef<HTMLTableElement, AppTableRootProps>(
  function RootPart(
    {
      basic,
      celled,
      structured,
      compact,
      striped,
      selectable,
      fixed,
      collapsing,
      singleLine,
      textAlign,
      verticalAlign,
      padded: _padded,
      unstackable: _unstackable,
      stackable: _stackable,
      sortable: _sortable,
      definition: _definition,
      inverted: _inverted,
      attached: _attached,
      size: _size,
      color: _color,
      columns: _columns,
      className,
      ...rest
    },
    ref,
  ) {
    return (
      <table
        ref={ref}
        {...domProps(rest)}
        className={cn(
          'sk-app-table',
          !basic && 'sk:rounded-card sk:bg-surface sk:shadow-card',
          basic === 'very' && 'sk-app-table--very-basic',
          (celled || structured) && 'sk-app-table--celled',
          compact && 'sk-app-table--compact',
          striped && 'sk-app-table--striped',
          selectable && 'sk-app-table--hover',
          singleLine && 'sk-app-table--single-line',
          fixed && 'sk:table-fixed',
          collapsing ? 'sk:w-auto' : 'sk:w-full',
          alignClasses({ textAlign, verticalAlign }),
          className,
        )}
      />
    );
  },
);

/* ------------------------------------------------------------ sections -- */

export type AppTableSectionProps = HTMLAttributes<HTMLTableSectionElement> & {
  /** Semantic's full-width footer; the footer spans the table regardless. */
  fullWidth?: boolean;
};

export const AppTableHeader = forwardRef<
  HTMLTableSectionElement,
  AppTableSectionProps
>(function HeaderPart({ fullWidth: _fullWidth, ...rest }, ref) {
  return <thead ref={ref} {...domProps(rest)} />;
});

export const AppTableBody = forwardRef<
  HTMLTableSectionElement,
  AppTableSectionProps
>(function BodyPart({ fullWidth: _fullWidth, ...rest }, ref) {
  return <tbody ref={ref} {...domProps(rest)} />;
});

export const AppTableFooter = forwardRef<
  HTMLTableSectionElement,
  AppTableSectionProps
>(function FooterPart({ fullWidth: _fullWidth, ...rest }, ref) {
  return <tfoot ref={ref} {...domProps(rest)} />;
});

/* ----------------------------------------------------------------- row -- */

export type AppTableRowProps = HTMLAttributes<HTMLTableRowElement> &
  AlignProps &
  ToneProps;

export const AppTableRow = forwardRef<HTMLTableRowElement, AppTableRowProps>(
  function RowPart(
    {
      textAlign,
      verticalAlign,
      singleLine,
      positive,
      negative,
      error,
      warning,
      active,
      disabled,
      className,
      ...rest
    },
    ref,
  ) {
    return (
      <tr
        ref={ref}
        {...domProps(rest)}
        className={cn(
          alignClasses({ textAlign, verticalAlign }),
          singleLine && 'sk-app-table__row--single-line',
          toneClasses({ positive, negative, error, warning, active, disabled }),
          className,
        )}
      />
    );
  },
);

/* --------------------------------------------------------------- cells -- */

export type AppTableCellProps = Omit<
  TdHTMLAttributes<HTMLTableCellElement>,
  'align' | 'valign' | 'width' | 'colSpan' | 'rowSpan'
> &
  CellLayoutProps &
  ToneProps;

function span(value: number | string | undefined) {
  if (value === undefined || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function cellClasses({
  textAlign,
  verticalAlign,
  singleLine,
  collapsing,
  selectable,
  className,
  ...tone
}: Omit<CellLayoutProps, 'colSpan' | 'rowSpan'> &
  ToneProps & { className?: string }) {
  return cn(
    alignClasses({ textAlign, verticalAlign, singleLine }),
    collapsing && 'sk:w-px sk:whitespace-nowrap',
    selectable && 'sk:cursor-pointer',
    toneClasses(tone),
    className,
  );
}

export const AppTableCell = forwardRef<HTMLTableCellElement, AppTableCellProps>(
  function CellPart(
    {
      textAlign,
      verticalAlign,
      singleLine,
      collapsing,
      selectable,
      width,
      colSpan,
      rowSpan,
      positive,
      negative,
      error,
      warning,
      active,
      disabled,
      className,
      style,
      ...rest
    },
    ref,
  ) {
    return (
      <td
        ref={ref}
        {...domProps(rest)}
        colSpan={span(colSpan)}
        rowSpan={span(rowSpan)}
        style={widthStyle(width, style)}
        className={cellClasses({
          textAlign,
          verticalAlign,
          singleLine,
          collapsing,
          selectable,
          positive,
          negative,
          error,
          warning,
          active,
          disabled,
          className,
        })}
      />
    );
  },
);

export type AppTableHeaderCellProps = Omit<
  ThHTMLAttributes<HTMLTableCellElement>,
  'align' | 'valign' | 'width' | 'colSpan' | 'rowSpan'
> &
  CellLayoutProps &
  ToneProps & {
    /** Semantic's sort state; prefer `aria-sort`. */
    sorted?: 'ascending' | 'descending';
  };

export const AppTableHeaderCell = forwardRef<
  HTMLTableCellElement,
  AppTableHeaderCellProps
>(function HeaderCellPart(
  {
    textAlign,
    verticalAlign,
    singleLine,
    collapsing,
    selectable,
    width,
    colSpan,
    rowSpan,
    positive,
    negative,
    error,
    warning,
    active,
    disabled,
    sorted,
    className,
    style,
    ...rest
  },
  ref,
) {
  return (
    <th
      ref={ref}
      aria-sort={sorted}
      {...domProps(rest)}
      colSpan={span(colSpan)}
      rowSpan={span(rowSpan)}
      style={widthStyle(width, style)}
      className={cellClasses({
        textAlign,
        verticalAlign,
        singleLine,
        collapsing,
        selectable,
        positive,
        negative,
        error,
        warning,
        active,
        disabled,
        className,
      })}
    />
  );
});
